/**
 * slot-id.ts — SlotId branded string type for widget placement.
 *
 * RESOLUTION-ORDER RULE (REVIEWS HIGH-2 closure):
 * The Zod union has THREE branches in this fixed order:
 *   1. z.enum(ENGINE_SLOT_IDS)   — engine slots match FIRST
 *   2. z.string().regex(AUTHOR_SLOT_RE)   — author-extension slots
 *   3. z.string().regex(ANCHOR_PATTERN)  — anchored author-section placements
 *
 * Engine slots take precedence on overlap. The string "main:above-flight-card"
 * appears in both ENGINE_SLOT_IDS (branch 1) and would match ANCHOR_PATTERN
 * (branch 3). Because branch 1 is listed first, the engine slot wins — no
 * widgetSlots[] lookup is performed for those strings at parse time.
 *
 * Authors who attempt to declare a widgetSlot with a slug that, combined with
 * the slot's zone, produces a string already in ENGINE_SLOT_IDS_SET are
 * rejected at the DECLARATION level by Plan 06's reserved-slug validator
 * (Pass 2C, REVIEWS-3 HIGH-4). This closes the shadowing risk one layer deeper.
 *
 * ANCHOR_PATTERN EXPORT (REVIEWS-3 MEDIUM-8 — HIGH/MEDIUM):
 * ANCHOR_PATTERN is exported as a module-level constant so Plan 06's
 * validators.ts can import it directly. Keeping the regex literal in two files
 * invited silent drift — if one changed (e.g., a new zone was added), the other
 * would silently diverge, causing validators to accept what the parser rejects
 * or vice-versa. The export closes this risk by construction.
 * See Plan 06 Task 2, Pass 2B for the validator cross-reference contract.
 *
 * isolatedDeclarations: the output type is declared as a TS type alias and
 * the Zod const is annotated with z.ZodType<SlotId> so tsc can emit declarations
 * without reading across module boundaries (D-15 paranoid-strict tsconfig).
 */

import { z } from "zod";

// ─── Engine slots ──────────────────────────────────────────────────────────

export const ENGINE_SLOT_IDS = [
  "options:top",
  "options:bottom",
  "main:floating-top",
  "main:above-flight-card",
  "main:below-flight-card",
  "main:above-reference",
  "main:below-reference",
] as const;

export type EngineSlotId = (typeof ENGINE_SLOT_IDS)[number];

// REVIEWS-3 LOW — Set form for O(1) lookup; Plan 06 validators.ts imports this constant.
export const ENGINE_SLOT_IDS_SET: ReadonlySet<EngineSlotId> = new Set<EngineSlotId>(
  ENGINE_SLOT_IDS,
);

// ─── Helper types for the anchored template literal ────────────────────────

export type EngineZone = "options" | "main";
export type Anchor = "above" | "below";

// ─── SlotId union type ─────────────────────────────────────────────────────

export type SlotId = EngineSlotId | `author:${string}` | `${EngineZone}:${Anchor}-${string}`;

// ─── Module-private regex constants ────────────────────────────────────────

// author-extension slots: `author:<kebab-case-slug>`
const AUTHOR_SLOT_RE = /^author:[a-z][a-z0-9-]*$/;

// REVIEWS-3 MEDIUM-8 — exported for Plan 06 validators.ts Pass 2B to import;
// keeping the regex literal in two files invited silent drift (HIGH/MEDIUM).
// Explicit RegExp annotation required by isolatedDeclarations.
export const ANCHOR_PATTERN: RegExp = /^(options|main):(above|below)-[a-z][a-z0-9-]*$/;

// ─── Zod schema — THREE-BRANCH resolution order, engine FIRST (REVIEWS HIGH-2) ───

// z.string().regex(…) always infers output type `string`, not the template-literal
// subtype. z.custom<SlotId>() lets us declare the exact output type while validating
// with a runtime function applying the three-branch resolution-order rule.
// Private variable pattern (per constraint.ts) satisfies isolatedDeclarations:
// the private const is fully inferrable; the exported const is re-typed via annotation.
const _slotIdValidator = (val: unknown): boolean => {
  if (typeof val !== "string") return false;
  // Branch 1: engine slots — MUST be checked first so engine slots win on overlap.
  if ((ENGINE_SLOT_IDS as readonly string[]).includes(val)) return true;
  // Branch 2: author-extension slots — `author:<kebab-case>`
  if (AUTHOR_SLOT_RE.test(val)) return true;
  // Branch 3: anchored author-section placement — `<zone>:<above|below>-<kebab-case>`
  // Cross-referenced against widgetSlots[].slug at Plan 06 Pass 2B validation.
  if (ANCHOR_PATTERN.test(val)) return true;
  return false;
};

const _slotIdSchema = z.custom<SlotId>(_slotIdValidator);

export const SlotId: z.ZodType<SlotId> = _slotIdSchema;
